<template>
	<nav class="nav-tabs">
		<ul class="nav-tabs__list">
			<li
				v-for="(item, i) in navItems"
				:key="item.key || i"
				:ref="el => setListRef(el, i)"
				:class="{
					'is-active': item.is_active,
					'is-disabled': item.is_disabled,
				}"
				class="nav-tabs__item"
			>
				<router-link v-if="!item.is_disabled" :to="item.to" class="nav-tabs__link">
					{{ item.label }}
				</router-link>

				<span v-else class="nav-tabs__link">
					{{ item.label }}
				</span>
			</li>
		</ul>

		<div ref="pill" class="nav-tabs__pill" :class="{ 'is-hidden': !isPillVisible }" aria-hidden />
	</nav>
</template>

<script lang="ts">
import gsap from 'gsap';
import { defineComponent, PropType, reactive, toRefs } from 'vue';

import { useArrayRefs } from '@/composables';
import { NavTabsItemModel } from '@/store/models/NavTabsItem';

const PILL_ANIMATION_DURATION = 700;

export default defineComponent({
	name: 'NavTabs',

	props: {
		items: {
			type: Array as PropType<NavTabsItemModel[]>,
			default: () => [],
		},
	},

	setup() {
		const data = reactive({
			isPillVisible: true,
			activeLink: null as null | NavTabsItemModel,
		});
		const { refsList: listElements, setListRef } = useArrayRefs();

		return {
			...toRefs(data),

			listElements,
			setListRef,
		};
	},

	computed: {
		pillElement(): HTMLDivElement {
			return this.$refs.pill as HTMLDivElement;
		},

		navItems(): NavTabsItemModel[] {
			const routeName = this.$route.name ?? '';

			return this.items.map(item => {
				item.is_active = !!item.matched_routes?.some(route => {
					return route === routeName;
				});

				if (item.is_active) {
					this.activeLink = item;
				}

				return item;
			});
		},
	},

	watch: {
		activeLink() {
			if (this.activeLink) {
				this.$nextTick(() => this.updateActivePillPosition(true));
			}
		},
	},

	created() {
		window.addEventListener('resize', this.onResize);
	},

	mounted() {
		this.updateActivePillPosition();
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.updateActivePillPosition();
		},

		activeListElement(): HTMLElement | undefined {
			return this.listElements.find(li => li.classList.contains('is-active'));
		},

		updateActivePillPosition(is_animated = false) {
			const activeListElement = this.activeListElement();

			if (!activeListElement) {
				this.isPillVisible = false;
				return;
			}

			if (!this.isPillVisible) {
				this.isPillVisible = true;
			}

			gsap.killTweensOf(this.pillElement);

			const targetProps = {
				width: activeListElement.offsetWidth,
				x: this.calculatePillOffset(activeListElement),
			};

			if (is_animated) {
				gsap.to(this.pillElement, {
					...targetProps,
					duration: PILL_ANIMATION_DURATION / 1000,
					ease: 'power4.out',
				});
			} else {
				gsap.set(this.pillElement, targetProps);
			}
		},

		calculatePillOffset(el: HTMLElement): number {
			return el.getBoundingClientRect().left - this.$el.getBoundingClientRect().left;
		},

		movePillTo(el: HTMLElement | undefined) {
			gsap.killTweensOf(this.pillElement);

			if (!el) {
				this.isPillVisible = false;
				return;
			}

			if (!this.isPillVisible) {
				gsap.set(this.pillElement, {
					width: el.offsetWidth,
					x: this.calculatePillOffset(el),
					scaleX: 1,
				});

				return;
			}

			gsap.to(this.pillElement, {
				width: el.offsetWidth,
				x: this.calculatePillOffset(el),
				ease: 'power4.out',
				duration: PILL_ANIMATION_DURATION / 1000,
			});
		},
	},
});
</script>

<style lang="scss">
.nav-tabs {
	$self: &;

	position: relative;
	border-radius: 1.2rem;
	background: $color-light;

	&__list {
		display: flex;
		height: 4.8rem;
		padding: 0.4rem;
		position: relative;
		z-index: 10;

		@include max-lg-and-portrait {
			height: 4.4rem;
		}
	}

	&__item {
		flex: 1;
		color: $color-gray;
		text-align: center;

		&.is-active,
		&--is-active {
			#{$self}__link {
				color: $color-light;
				transition: color 300ms ease;
			}
		}

		&.is-disabled,
		&--is-disabled {
			#{$self}__link {
				color: rgba($color-gray, 0.5);
				pointer-events: none;
			}
		}
	}

	&__pill {
		height: 4rem;
		position: absolute;
		top: 0.4rem;
		left: 0;
		z-index: 1;
		transform: translateX(0.4rem);
		border-radius: 0.8rem;
		background: $color-dark;

		@include max-lg-and-portrait {
			height: 3.6rem;
		}

		&.is-hidden {
			display: none;
		}
	}

	&__link {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		padding: 0.2rem 0.5rem;
		color: inherit;
		color: rgba($color-dark, 0.8);
		font-size: 1.5rem;
		font-weight: 500;
		line-height: 1.15;
		text-align: center;
		text-decoration: none;
		transition: color 300ms ease-out;
		opacity: 1;
		border-radius: 0.8rem;

		&:hover {
			color: rgba($color-dark, 1);
		}
	}
}
</style>
